import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { BookingFilterDto } from '@opb-workspace/shared';
import { formatDateForApi } from '../../../core/utils/date.utils';

export interface BookingsState {
  filters: BookingFilterDto;
  lastUpdated: number;
}

@Injectable({
  providedIn: 'root'
})
export class BookingsStateService {
  private readonly STATE_KEY = 'bookings_state';
  private readonly STATE_EXPIRY_HOURS = 24;

  private readonly defaultState: BookingsState = {
    filters: {
      page: 1,
      limit: 10,
    },
    lastUpdated: Date.now()
  };

  private stateSubject = new BehaviorSubject<BookingsState>(this.defaultState);

  constructor() {
    this.initializeState();
  }

  private initializeState(): void {
    const savedState = this.loadStateFromStorage();
    if (savedState && !this.isStateExpired(savedState.lastUpdated)) {
      this.stateSubject.next(savedState);
    }
  }

  private isStateExpired(lastUpdated: number): boolean {
    const now = Date.now();
    const expiryTime = this.STATE_EXPIRY_HOURS * 60 * 60 * 1000;
    return (now - lastUpdated) > expiryTime;
  }

  getState(): Observable<BookingsState> {
    return this.stateSubject.asObservable();
  }

  getCurrentState(): BookingsState {
    return this.stateSubject.value;
  }

  updateState(partialState: Partial<BookingsState>): void {
    const currentState = this.stateSubject.value;
    const newState: BookingsState = {
      ...currentState,
      ...partialState,
      lastUpdated: Date.now()
    };
    this.stateSubject.next(newState);
    this.saveStateToStorage(newState);
  }

  updateFilters(filters: Partial<BookingFilterDto>): void {
    const currentState = this.getCurrentState();
    const isPaginationChange = 'page' in filters || 'limit' in filters;

    this.updateState({
      filters: {
        ...currentState.filters,
        ...filters,
        // Reset to page 1 if any filter other than pagination changes, unless explicitly provided
        page: isPaginationChange ? (filters.page || currentState.filters.page) : 1
      }
    });
  }

  clearState(): void {
    this.stateSubject.next(this.defaultState);
    this.clearStateFromStorage();
  }

  private saveStateToStorage(state: BookingsState): void {
    try {
      localStorage.setItem(this.STATE_KEY, JSON.stringify(state));
    } catch (error) {
      console.warn('Error saving Bookings state to localStorage:', error);
    }
  }

  private loadStateFromStorage(): BookingsState | null {
    try {
      const saved = localStorage.getItem(this.STATE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.warn('Error loading Bookings state from localStorage:', error);
    }
    return null;
  }

  private clearStateFromStorage(): void {
    try {
      localStorage.removeItem(this.STATE_KEY);
    } catch (error) {
      console.warn('Error clearing Bookings state from localStorage:', error);
    }
  }

  stateToQueryParams(state: BookingsState): any {
    const params: any = {};
    const filters = state.filters;

    Object.keys(filters).forEach(key => {
      const value = filters[key as keyof BookingFilterDto];
      if (value !== undefined && value !== null && value !== '') {
        const val = value as unknown;
        if (val instanceof Date) {
          params[key] = formatDateForApi(val);
        } else {
          params[key] = String(value);
        }
      }
    });

    // Ensure pagination
    params.page = (filters.page || 1).toString();
    params.limit = (filters.limit || 10).toString();

    return params;
  }

  queryParamsToState(params: any): BookingsState {
    const filters: BookingFilterDto = {};
    const numericKeys = ['page', 'limit'];

    Object.keys(params).forEach(key => {
      if (numericKeys.includes(key)) {
        // Safe casting for DTO key but value is number
        (filters as any)[key] = Number(params[key]);
      } else {
        (filters as any)[key] = params[key];
      }
    });

    // Defaults
    if (!filters.page) filters.page = 1;
    if (!filters.limit) filters.limit = 10;

    return {
      filters,
      lastUpdated: Date.now()
    };
  }
}
